import { ref } from 'vue';
import { useTrayViewStore } from '@/js/core/stores/tray-view-store';
import {
  createComment,
  deleteComment,
  updateComment as apiUpdateComment,
  getTrayComments,
} from '@/js/core/utils/trays-api';

export function useTrayComments({ trayUuid, markRefreshed }) {
  const trayViewStore = useTrayViewStore();

  const editorRef = ref(null);
  const commentMediaRef = ref(null);
  const commentMedia = ref([]);
  const isSubmitting = ref(false);
  const submitError = ref('');

  const handlePostComment = async () => {
    const content = editorRef.value?.getContent()?.trim();
    const hasContent = content && content !== '<br>';
    const hasMedia = commentMedia.value.length > 0;

    if (!hasContent && !hasMedia) {
      submitError.value = 'Add text or an attachment before posting.';
      return;
    }

    submitError.value = '';

    if (hasMedia && commentMediaRef.value?.validateMediaMetadata?.() === false) {
      submitError.value =
        'Add the required description for each attachment and remove unsupported media.';
      return;
    }

    isSubmitting.value = true;

    try {
      const uuid = trayUuid.value;
      if (!uuid) {
        submitError.value = 'No tray ID provided';
        return;
      }

      const data = { content: hasContent ? content : '' };

      if (hasMedia) {
        data.media = commentMedia.value.map(({ id, alt, caption }) => ({
          id,
          alt: alt || '',
          caption: caption || '',
        }));
      }

      const response = await createComment(uuid, data);
      const newComment = response.data;
      trayViewStore.addComment(newComment);
      editorRef.value?.clear();
      commentMedia.value = [];
      markRefreshed();
    } catch (err) {
      submitError.value = err.message || 'Failed to post comment';
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleDeleteComment = async commentUuid => {
    try {
      const uuid = trayUuid.value;
      if (!uuid) {
        submitError.value = 'No tray ID provided';
        return;
      }

      await deleteComment(uuid, commentUuid);
      trayViewStore.deleteComment(commentUuid);
      markRefreshed();
    } catch (err) {
      submitError.value = err.message || 'Failed to delete comment';
    }
  };

  const handleUpdateComment = async ({ uuid, content, media, onSuccess, onError }) => {
    const trayUuidValue = trayUuid.value;
    if (!trayUuidValue) {
      const message = 'No tray ID provided';
      submitError.value = message;
      onError?.(message);
      return;
    }

    try {
      const data = {};
      if (content !== undefined) data.content = content;
      if (media !== undefined) data.media = media;

      const response = await apiUpdateComment(trayUuidValue, uuid, data);
      const updatedComment = response.data;

      trayViewStore.updateComment(uuid, updatedComment);
      markRefreshed();
      onSuccess?.(updatedComment);
    } catch (err) {
      const message = err.message || 'Failed to update comment';
      submitError.value = message;
      onError?.(message);
    }
  };

  const handleRefreshComments = async () => {
    try {
      const uuid = trayUuid.value;
      if (!uuid) {
        submitError.value = 'No tray ID provided';
        return;
      }

      const commentsResult = await getTrayComments(uuid);
      trayViewStore.setFromApi(
        trayViewStore.tray,
        commentsResult.data,
        commentsResult.meta?.pagination || null
      );
      markRefreshed();
    } catch (err) {
      submitError.value = err.message || 'Failed to refresh comments';
    }
  };

  return {
    editorRef,
    commentMediaRef,
    commentMedia,
    isSubmitting,
    submitError,
    handlePostComment,
    handleDeleteComment,
    handleUpdateComment,
    handleRefreshComments,
  };
}
